import React, { useEffect, useState } from 'react';
import { cookieClickHandler } from '../STW/utility';
import { IDRTemplate } from '.';
import CouponDetails from './components/couponDetails';
import UserDetails from './components/userDetails';
import CrossIconMobile from '../../icons/crossIconMobile';
import CrossIconDesktop from '../../icons/crossIconDesktop';
import useIsMobile from '../../hooks/useIsMobile';
import {
  BACKGROUND_TYPE,
  IDRWidgetCustomisation,
  LANGUAGE_VALUES,
  LANGUAGE_ISO_CODES,
} from '@bikdotai/bik-models/dm';
import Fab, { IFab } from '../../components/Fab';
import Send from '../../components/Icons/Send';
import { ComponentStyleMap, injectFontLink } from '../../utilities/styleUtils';
import { LanguageUtils } from 'utilities/languageUtilities';
import { fetchPositionCss } from '../CaptivateBanner/utils';

const DirectRewardTemplate = (props: IDRTemplate) => {
  const {
    preview,
    showComponents,
    onCloseHandle,
    widgetCustomisation,
    error,
    isLoading,
    isMobile,
    claimPrizeHandle,
    visibility,
    leadGeneration,
    discountCodeDetails,
    templateName,
    containerReference,
    setShowComponents,
    environment,
  } = props;
  const [currentStyle, setCurrentStyle] = useState<any>(null);
  const languageUtils = new LanguageUtils();
  const [widgetLanguage, setWidgetLanguage] = useState(LANGUAGE_VALUES.ENGLISH);
  const isMobileDevice = useIsMobile();
  const multiLingualData =
    widgetCustomisation?.multiLingualConfigurations?.[widgetLanguage];

  useEffect(() => {
    if (widgetCustomisation.fontFamily) {
      injectFontLink([widgetCustomisation.fontFamily?.link]);
    }
  }, [widgetCustomisation.fontFamily]);

  let imageUrl: string;
  if (preview) {
    if (isMobile) {
      imageUrl = showComponents.userDetails
        ? (multiLingualData?.playScreenImageMobile ??
          (Object.keys(multiLingualData ?? {}).includes('playScreenImageMobile')
            ? ''
            : widgetCustomisation.playScreenImageMobile))
        : (multiLingualData?.postPlayScreenImageMobile ??
          (Object.keys(multiLingualData ?? {}).includes(
            'postPlayScreenImageMobile',
          )
            ? ''
            : widgetCustomisation.postPlayScreenImageMobile));
    } else {
      imageUrl = showComponents.userDetails
        ? (multiLingualData?.playScreenImageDesktop ??
          (Object.keys(multiLingualData ?? {}).includes(
            'playScreenImageDesktop',
          )
            ? ''
            : widgetCustomisation.playScreenImageDesktop))
        : (multiLingualData?.postPlayScreenImageDesktop ??
          (Object.keys(multiLingualData ?? {}).includes(
            'postPlayScreenImageDesktop',
          )
            ? ''
            : widgetCustomisation.postPlayScreenImageDesktop));
    }
  } else {
    if (isMobileDevice) {
      imageUrl = showComponents.userDetails
        ? (multiLingualData?.playScreenImageMobile ??
          (Object.keys(multiLingualData ?? {}).includes('playScreenImageMobile')
            ? ''
            : widgetCustomisation.playScreenImageMobile))
        : (multiLingualData?.postPlayScreenImageMobile ??
          (Object.keys(multiLingualData ?? {}).includes(
            'postPlayScreenImageMobile',
          )
            ? ''
            : widgetCustomisation.postPlayScreenImageMobile));
    } else {
      imageUrl = showComponents.userDetails
        ? (multiLingualData?.playScreenImageDesktop ??
          (Object.keys(multiLingualData ?? {}).includes(
            'playScreenImageDesktop',
          )
            ? ''
            : widgetCustomisation.playScreenImageDesktop))
        : (multiLingualData?.postPlayScreenImageDesktop ??
          (Object.keys(multiLingualData ?? {}).includes(
            'postPlayScreenImageDesktop',
          )
            ? ''
            : widgetCustomisation.postPlayScreenImageDesktop));
    }
  }

  useEffect(() => {
    if (preview && showComponents) {
      if (showComponents.userDetails) {
        if (isMobile) {
          ComponentStyleMap[templateName]?.userInputPreviewMobile.then(
            async style => {
              setCurrentStyle((await style()).default);
            },
          );
        } else {
          ComponentStyleMap[templateName]?.userInputPreviewDesktop.then(
            async style => {
              setCurrentStyle((await style()).default);
            },
          );
        }
      } else if (showComponents.couponDetails) {
        if (isMobile) {
          ComponentStyleMap[templateName]?.thankyouPreviewMobile.then(
            async style => {
              setCurrentStyle((await style()).default);
            },
          );
        } else {
          ComponentStyleMap[templateName]?.thankyouPreviewDesktop.then(
            async style => {
              setCurrentStyle((await style()).default);
            },
          );
        }
      }
    } else {
      ComponentStyleMap[templateName]?.globalStyle.then(async style => {
        setCurrentStyle((await style()).default);
      });
    }
  }, [preview, isMobile]);

  useEffect(() => {
    const isBikURL =
      window.location.href?.includes('https://dashboard.bik.ai/') ||
      window.location.href?.includes('https://staging.dashboard.bik.ai/');
    const previewLanguage = props?.widgetCustomisation?.previewLanguage;
    const storeLanguage =
      (isBikURL ? previewLanguage : window?.Shopify?.locale) ??
      previewLanguage ??
      LANGUAGE_ISO_CODES.EN;
    setWidgetLanguage(languageUtils.getStoreLanguage(storeLanguage));
  }, [window.location.href, props?.widgetCustomisation?.previewLanguage]);

  const fabStyle: IFab = {
    info: undefined,
    visibility: undefined,
    icon: <Send color={props.widgetCustomisation?.iconColor} />,
    onClick() {
      cookieClickHandler(
        `bik-${props.id}-clicked`,
        'bikclick',
        'WIDGET_CLICKED',
        1,
        props.id,
        props.environment,
      );
      setShowComponents({
        userDetails: true,
        floatingButton: false,
        couponDetails: false,
      });
    },
    buttonStyle: {},
    isMobile: props.isMobile,
    preview: props.preview ?? false,
    widgetCustomisation: {
      fabText: props.widgetCustomisation?.fabText,
      iconColor: props.widgetCustomisation?.iconColor,
      fabTransform: props.widgetCustomisation?.fabTransform,
      fabPositioner: props.widgetCustomisation?.fabPositioner,
      fabBackgroundColor: props.widgetCustomisation?.fabBackgroundColor,
      fabLottie: props.widgetCustomisation?.fabLottie,
      fabAnimationRun: props.widgetCustomisation?.fabAnimationRun,
      fabImage: props.widgetCustomisation?.fabImage,
      fabIconLink: props.widgetCustomisation?.fabIconLink,
      fabWebSize: props.widgetCustomisation?.fabWebSize,
      fabMobileSize: props.widgetCustomisation?.fabMobileSize,
    } as any,
  };

  if (!currentStyle) {
    return <></>;
  }

  return (
    <>
      {(showComponents.userDetails || showComponents.couponDetails) && (
        <div className={currentStyle.bikDirectReward}>
          <div className={currentStyle.overlay}></div>
          <div
            className={currentStyle.bikDRParentContainer}
            ref={containerReference}
            style={{
              overflow: isMobile ? 'visible' : 'auto',
              width: isMobile || isMobileDevice || imageUrl ? '100%' : '40%',
              ...fetchPositionCss(
                props.widgetCustomisation,
                isMobile || isMobileDevice,
                true,
              ),
            }}
          >
            <div
              className={
                currentStyle.bikDRMainContainer +
                ' ' +
                currentStyle.scaleUpEffect
              }
              id={currentStyle.bikDirectRewardMainContainer}
            >
              <div
                className={currentStyle.bikDRImageDiv}
                style={{
                  width: imageUrl
                    ? '40%'
                    : isMobile || isMobileDevice
                      ? '100%'
                      : '0',
                }}
              >
                <div
                  className={currentStyle.bikDRCloseBtnWrapperMobile}
                  onClick={onCloseHandle}
                >
                  <CrossIconMobile />
                </div>
                {imageUrl && (
                  <img
                    className={currentStyle.bikDRImageContainer}
                    src={imageUrl}
                    onError={e => {
                      (e.target as HTMLImageElement).onerror = null;
                      (e.target as HTMLImageElement).src =
                        'https://firebasestorage.googleapis.com/v0/b/bikayi-chat.appspot.com/o/images-uid%2F509f5b9f-4b8d-455b-af85-b97b0d4b41d7?alt=media&token=858d569b-a3b5-46f9-9bb9-b88261b0bcd5';
                    }}
                  />
                )}
              </div>
              <div
                className={currentStyle.bikDRContentContainer}
                id={currentStyle.directRewardClaimRewardContainer}
                style={
                  widgetCustomisation.backgroundType === BACKGROUND_TYPE.IMAGE
                    ? {
                        background: `url(${isMobile || isMobileDevice ? widgetCustomisation.backgroundImageMobile : widgetCustomisation.backgroundImageDesktop})`,
                        backgroundSize: '100% 100%',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        width:
                          isMobile || isMobileDevice || !imageUrl
                            ? '100%'
                            : '60%',
                      }
                    : {
                        backgroundColor: widgetCustomisation.backgroundColour,
                        fontFamily: widgetCustomisation.fontFamily.name,
                        width:
                          isMobile || isMobileDevice || !imageUrl
                            ? '100%'
                            : '60%',
                      }
                }
              >
                <div
                  id={currentStyle.directRewardCloseBtnWrapper}
                  onClick={onCloseHandle}
                >
                  <CrossIconDesktop color={widgetCustomisation?.fontColour} />
                </div>
                {showComponents.userDetails ? (
                  <UserDetails
                    widgetCustomisation={
                      widgetCustomisation as IDRWidgetCustomisation
                    }
                    visibility={visibility}
                    leadGeneration={leadGeneration}
                    error={error}
                    isLoading={isLoading}
                    claimPrizeHandle={claimPrizeHandle}
                    preview={preview}
                    templateName={templateName}
                    isMobile={isMobile}
                    isMobileDevice={isMobileDevice}
                    id={props.id}
                    countryCode={props.countryCode}
                    environment={environment}
                    setShowComponents={setShowComponents}
                    widgetLanguage={widgetLanguage}
                  />
                ) : (
                  <></>
                )}
                {showComponents.couponDetails ? (
                  <CouponDetails
                    discountCodeDetails={discountCodeDetails}
                    preview={preview}
                    widgetCustomisation={widgetCustomisation}
                    templateName={templateName}
                    isMobile={isMobile}
                    id={props.id}
                    environment={environment}
                    setShowComponents={setShowComponents}
                    visibility={visibility}
                    widgetLanguage={widgetLanguage}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showComponents.floatingButton && (
        <Fab {...fabStyle} id={`bik-stw-${props.id}-fab`} />
      )}
    </>
  );
};

export default DirectRewardTemplate;
